import React from 'react';
import { Bold, Italic, Code2, RemoveFormatting } from 'lucide-react';
import { Button } from '../../../ui/button';
import { cn } from '../../../ui/utils';
import { useStableCallback } from '../../../../hooks';

const ToolbarButton = ({ active, className, children, title, ...props }) => (
  <Button
    type="button"
    variant="ghost"
    size="sm"
    title={title}
    aria-label={title}
    aria-pressed={active}
    className={cn(
      'h-8 shrink-0 gap-1.5 rounded-md px-2.5 text-muted-foreground hover:bg-background hover:text-foreground',
      active && 'bg-background text-foreground shadow-sm ring-1 ring-border',
      className
    )}
    {...props}
  >
    {children}
  </Button>
);

const ToolbarDivider = () => (
  <span className="mx-0.5 hidden h-5 w-px shrink-0 bg-border sm:block" aria-hidden />
);

const MenuBar = ({ editor }) => {
  const headingValue = editor?.isActive('heading', { level: 1 })
    ? '1'
    : editor?.isActive('heading', { level: 2 })
      ? '2'
      : editor?.isActive('heading', { level: 3 })
        ? '3'
        : editor?.isActive('heading', { level: 4 })
          ? '4'
          : editor?.isActive('heading', { level: 5 })
            ? '5'
            : 'normal';

  const onSelectHeading = useStableCallback((e) => {
    const selected = e.target.value;
    if (selected === 'normal') {
      editor.chain().focus().setParagraph().run();
      return;
    }
    editor.chain().focus().toggleHeading({ level: Number(selected) }).run();
  });

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-toolbar flex flex-wrap items-center gap-1.5 sm:gap-2">
      <select
        name="heading-select-tiptap"
        value={headingValue}
        onChange={onSelectHeading}
        aria-label="Text style"
        className={cn(
          'h-8 max-w-[9.5rem] shrink-0 cursor-pointer rounded-md border border-border bg-background',
          'px-2 text-xs font-medium text-foreground outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring/40'
        )}
      >
        <option value="normal">Normal</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
        <option value="5">Heading 5</option>
      </select>

      <ToolbarDivider />

      <div className="flex items-center gap-1 rounded-lg border border-border/70 bg-background/80 p-0.5">
        <ToolbarButton
          title="Bold"
          active={editor.isActive('bold')}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          onClick={() => editor.chain().focus().toggleBold().run()}
          className="min-w-8 px-2 font-bold"
        >
          <Bold className="h-4 w-4" strokeWidth={2.5} />
        </ToolbarButton>
        <ToolbarButton
          title="Italic"
          active={editor.isActive('italic')}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className="min-w-8 px-2 italic"
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
      </div>

      <ToolbarDivider />

      <ToolbarButton
        title="Code block"
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className="border border-border/70 bg-background/80 px-2.5 hover:bg-background"
      >
        <Code2 className="h-4 w-4" />
        <span className="hidden text-xs font-medium sm:inline">Code block</span>
      </ToolbarButton>

      <ToolbarButton
        title="Clear formatting"
        onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        className="ml-auto border border-transparent px-2.5 text-muted-foreground hover:border-border/70 hover:bg-background"
      >
        <RemoveFormatting className="h-4 w-4" />
        <span className="hidden text-xs font-medium sm:inline">Clear</span>
      </ToolbarButton>
    </div>
  );
};

export default MenuBar;
